// Fetches the rollup payload for the current filters — the counterpart to
// useSchedulingData, which fetches raw rows.
//
// The query key carries granularity, drill level and scope, unlike the raw hook's
// key. It has to: the server decides which sources answer from exactly those, so
// two requests differing only in granularity are genuinely different payloads.
// Keying on the date range alone would serve a Week response to a Day request and
// the rollup rows would silently be the wrong grain.

import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { useDataStamp } from "@/hooks/useDataStamp";
import type { FilterState, Granularity } from "@/types/filters";
import { EMPTY_RANGE, getPriorYearRange } from "@/lib/periods";
import type { DvmWeekRow, RollupSiteWeekRow } from "@/lib/rollupMetrics";
import type { SchedulingRow } from "@/types/scheduling";

export interface RollupPayload {
  source: string;
  thresholds: { seen: number; avail: number };
  wideDvmRows: boolean;
  siteWeeks: RollupSiteWeekRow[];
  dvmWeeks: DvmWeekRow[];
  edgeRows: SchedulingRow[];
}

interface UseRollupDataResult {
  payload: RollupPayload | null;
  loading: boolean;
  /** See useSchedulingData: true during background refetches too, not just the first load. */
  fetching: boolean;
  error: ApiError | Error | null;
}

export function useRollupData(
  filters: FilterState,
  granularity: Granularity,
  enabled: boolean
): UseRollupDataResult {
  const { customStart, customEnd, period, drillLevel, scope, launchedBefore } = filters;
  const pyRange =
    getPriorYearRange({ startDate: customStart, endDate: customEnd }, period) ?? EMPTY_RANGE;
  const { stamp: dataVersion, ready } = useDataStamp();

  const params = new URLSearchParams({
    start: customStart,
    end: customEnd,
    pyStart: pyRange.startDate,
    pyEnd: pyRange.endDate,
    period,
    granularity,
    drillLevel,
    v: dataVersion,
  });
  // Only non-null scope values, so the key and the URL stay stable rather than
  // carrying "null" strings that the server would treat as a filter.
  if (scope.countryCode) params.set("countryCode", scope.countryCode);
  if (scope.divisionName) params.set("divisionName", scope.divisionName);
  if (scope.groupName) params.set("groupName", scope.groupName);
  if (scope.locationId) params.set("locationId", scope.locationId);
  if (scope.resourceId) params.set("resourceId", scope.resourceId);
  // Unlike scope, this one narrows the payload in SQL -- so it belongs in the
  // query key as much as the date range does.
  if (launchedBefore) params.set("launchedBefore", launchedBefore);

  const { data, isPending, isFetching, error } = useQuery({
    queryKey: ["scheduling-rollup", params.toString()],
    queryFn: () => api<RollupPayload>(`/api/scheduling/rollup?${params}`),
    enabled: enabled && ready,
  });

  return {
    payload: data ?? null,
    // isPending is true for a disabled query, which would leave the dashboard
    // showing a spinner forever in raw mode.
    loading: enabled && isPending,
    fetching: enabled && isFetching,
    error: (error as ApiError | Error | null) ?? null,
  };
}
